import { getTextbook, getAvailableGrades, getAvailableTerms, getAllWords } from '../data/textbooks';

/**
 * 课本数据服务
 * 提供统一的接口来获取和管理课本数据
 */
class TextbookService {
  /**
   * 获取所有可用年级
   * @returns {string[]} 年级列表
   */
  getGrades() {
    return getAvailableGrades();
  }
  
  /**
   * 获取指定年级的可用学期
   * @param {string} grade 年级
   * @returns {string[]} 学期列表
   */
  getTerms(grade) {
    return getAvailableTerms(grade);
  }
  
  /**
   * 获取指定年级和学期的课本数据
   * @param {string} grade 年级
   * @param {string} term 学期
   * @returns {Object|null} 课本数据
   */
  getTextbook(grade, term) {
    return getTextbook(grade, term);
  }
  
  /**
   * 获取指定年级和学期的所有单元
   * @param {string} grade 年级
   * @param {string} term 学期
   * @returns {Object[]} 单元列表
   */
  getUnits(grade, term) {
    const textbook = this.getTextbook(grade, term);
    return textbook ? textbook.units : [];
  }
  
  /**
   * 获取指定年级、学期和单元的所有单词
   * @param {string} grade 年级
   * @param {string} term 学期
   * @param {string} unitName 单元名称
   * @returns {Object[]} 单词列表
   */
  getWordsFromUnit(grade, term, unitName) {
    const textbook = this.getTextbook(grade, term);
    if (!textbook) return [];
    
    const unit = textbook.units.find(u => u.name === unitName);
    return unit ? unit.words : [];
  }
  
  /**
   * 获取符合条件的所有单词
   * @param {string|null} gradeFilter 年级过滤条件，null表示所有年级
   * @param {string|null} termFilter 学期过滤条件，null表示所有学期
   * @param {number|null} difficultyFilter 难度过滤条件，null表示所有难度
   * @returns {Object[]} 单词列表
   */
  getAllWords(gradeFilter = null, termFilter = null, difficultyFilter = null) {
    let words = getAllWords(gradeFilter, termFilter);
    
    if (difficultyFilter !== null) {
      words = words.filter(word => word.difficulty <= difficultyFilter);
    }
    
    return words;
  }
  
  /**
   * 随机获取指定数量的单词
   * @param {number} count 单词数量
   * @param {Object} filters 过滤条件
   * @param {string|null} filters.grade 年级过滤条件
   * @param {string|null} filters.term 学期过滤条件
   * @param {number|null} filters.difficulty 难度过滤条件
   * @returns {Object[]} 随机单词列表
   */
  getRandomWords(count, filters = {}) {
    const { grade = null, term = null, difficulty = null } = filters;
    
    // 获取符合条件的所有单词
    let allWords = this.getAllWords(grade, term, difficulty);
    
    // 如果没有符合条件的单词，返回空数组
    if (allWords.length === 0) return [];
    
    // 随机选择指定数量的单词
    const randomWords = [];
    const wordCount = Math.min(count, allWords.length);
    
    // 复制一份单词列表，避免修改原数组
    const wordsCopy = [...allWords];
    
    for (let i = 0; i < wordCount; i++) {
      // 随机选择一个索引
      const randomIndex = Math.floor(Math.random() * wordsCopy.length);
      // 添加到结果数组
      randomWords.push(wordsCopy[randomIndex]);
      // 从复制的数组中移除已选单词，避免重复
      wordsCopy.splice(randomIndex, 1);
    }
    
    return randomWords;
  }
  
  /**
   * 格式化单词列表，添加UI相关属性
   * @param {Object[]} words 单词列表
   * @param {Object} options 格式化选项
   * @param {boolean} options.selected 是否选中
   * @param {Object} options.colorMap 单元颜色映射
   * @returns {Object[]} 格式化后的单词列表
   */
  formatWordsForUI(words, options = {}) {
    const { selected = false, colorMap = {} } = options;
    
    // 默认颜色映射
    const defaultColorMap = {
      'Unit 1': 'blue',
      'Unit 2': 'green',
      'Unit 3': 'yellow',
      'Unit 4': 'red',
      'Unit 5': 'purple',
      'Unit 6': 'orange',
      'Unit 7': 'pink',
      'Unit 8': 'teal'
    };
    
    // 合并默认颜色映射和自定义颜色映射
    const mergedColorMap = { ...defaultColorMap, ...colorMap };
    
    return words.map(word => ({
      ...word,
      selected,
      color: mergedColorMap[word.unitName] || 'gray'
    }));
  }
}

// 创建单例实例
const textbookService = new TextbookService();

export default textbookService;